import {DateTime} from "luxon";
import {granularityMetadata} from "~/components/reports/granularitySelector";
import {WidgetLoadingState, type EditableWidget, type Widget} from "~/components/reports/widgetSpecific/widgets";
import {WidgetType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";
import {calculateGridDerivedState} from "~/modules/reports/layout/reportLayoutHelpers";
import {
    DateRangeOptions,
    Granularity,
    TemplatePlacement,
    WidgetValueType,
} from "~/modules/reports/domain/reportTypes";
import {CanvasControlType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";
import type {DatasetMetadata} from "~/modules/reports/domain/analyticsDatasetTypes";
import type {
    DraggingGhostProps,
    EditableCanvasControl,
    EditableReportPage,
    ResizeHandlePosition,
} from "~/modules/reports/domain/reportTypes";
import {
    getDatasetIdsForCanvasControl,
    isStaticCanvasControl,
    getDatesForDateRangeOption,
    getComparisonDateRangeForPrimaryRange,
    reconcileTableColumnProperties,
    refreshReportPageIds,
    reportPageToEditableReportPage,
} from "~/modules/reports/utilities/reportUtilities";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {distinct, generateUuid} from "@vesta/global-common-typescript/utilities/utilities";
import type {AtomicFilter, BackendFilter} from "~/typeDefinitions";
import {BackendFilterOperations, BackendFilterTypes, BackendFilterValueTypes} from "~/typeDefinitions";
import type {ReportPage} from "~/domainModels/reports/reportPage";
import type {CanvasControl, CanvasControls} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";
import type {ReportPageTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportTemplate";

export type EditableReportState = {
    pages: Array<EditableReportPage>;
    currentlySelectedWidget: EditableWidget | null;
    currentlySelectedPageId: Uuid | null;
    leftPane: {
        currentlySelectedTabIndex: number;
    };
    rightPane: {
        currentlySelectedTabIndex: number;
    };
    pageIndexToDelete?: number;
    pageIndexToDuplicate?: number;
    clearPageIndex?: number;
    isReportNameEditing: boolean;
    title: string;
    isResizing: boolean;
    isDragging: boolean;
    resizeHandlePosition: ResizeHandlePosition | null;
    draggedCellProps: {
        row: number;
        col: number;
    } | null;
    ghostProps: DraggingGhostProps | null;
    isTemplateDragging: boolean;
    canvasControls: CanvasControls;
    datasets: Array<DatasetMetadata>;
    widgetToDelete: EditableWidget | null;
    destructiveWidgetTypeChangeDetails: {
        currentWidget: EditableWidget;
        newWidgetType: WidgetType;
    } | null;
    doUnsavedChangesExist: boolean;
    requiredL0SkillTemplates: Array<Uuid>;
    timezone: string;
    datasetIdHashSet: Set<Uuid>;
    companyId: Uuid;
};

function reportPageTemplateToReportPage(reportPageTemplate: ReportPageTemplate): ReportPage {
    return {
        ...reportPageTemplate,
        widgets: reportPageTemplate.widgets.map((widgetTemplate) => {
            const {datasetTemplateId: _datasetTemplateId, ...widget} = widgetTemplate as typeof widgetTemplate & {
                datasetTemplateId?: Uuid | null;
            };

            return widget as Widget;
        }),
    };
}

export enum EditableReportActionType {
    editWidget,
    setCurrentlySelectedWidget,
    setCurrentlySelectedPage,
    addWidget,
    changeLeftPaneTab,
    addBlankPage,
    setDeletePageIndex,
    deletePage,
    swapPages,
    changePageDates,
    deleteWidget,
    editPage,
    editReport,
    changeRightPaneTab,
    useTemplate,
    setDuplicatePageIndex,
    addDuplicatePage,
    updateResizeHandlePosition,
    updateIsResizing,
    updateIsDragging,
    updateDraggedCellProps,
    updateDraggingGhostProperties,
    setIsTemplateDragging,
    addTemplate,
    // editPageDateRange,
    editWidgetDateRange,
    addCanvasControl,
    editCanvasControl,
    editFilter,
    addFilter,
    deleteFilter,
    setWidgetToDelete,
    destructiveWidgetTypeChangeWidget,
    setCanvasControls,
    toggleCanvasControlActiveStatus,
    applyCanvasControlToApplicableWidgets,
    removeUnusedCanvasControls,
    generateRequiredL0SkillTemplates,
    handleGlobalFiltersLinking,
    handleCanvasControlSelectAll,
    setClearPageIndex,
    clearPage,
}

export type EditableReportAction =
    | {
          actionType: EditableReportActionType.editWidget;
          widget: EditableWidget;
          changedFields: Partial<EditableWidget>;
      }
    | {
          actionType: EditableReportActionType.setCurrentlySelectedWidget;
          widget: EditableWidget | null;
      }
    | {
          actionType: EditableReportActionType.setCurrentlySelectedPage;
          pageId: Uuid | null;
      }
    | {
          actionType: EditableReportActionType.addWidget;
          pageIndex: number;
          widget: EditableWidget;
      }
    | {
          actionType: EditableReportActionType.changeLeftPaneTab;
          index: number;
      }
    | {
          actionType: EditableReportActionType.addBlankPage;
          nCols: number;
          nRows: number;
      }
    | {
          actionType: EditableReportActionType.deletePage;
          deleteIndex: number;
      }
    | {
          actionType: EditableReportActionType.swapPages;
          fromIndex: number;
          toIndex: number;
      }
    | {
          actionType: EditableReportActionType.setDeletePageIndex;
          pageIndex: number | null;
      }
    // | {
    //       actionType: EditableReportActionType.changePageDates;
    //       startDate: Date;
    //       endDate: Date;
    //       pageIndex: number;
    //   }
    | {
          actionType: EditableReportActionType.deleteWidget;
          currentlySelectedWidget: EditableWidget | null;
      }
    | {
          actionType: EditableReportActionType.editPage;
          page: EditableReportPage;
          changedFields: Partial<EditableReportPage>;
      }
    | {
          actionType: EditableReportActionType.editReport;
          changedFields: Partial<EditableReportState>;
      }
    | {
          actionType: EditableReportActionType.changeRightPaneTab;
          tabIndex: number;
      }
    | {
          actionType: EditableReportActionType.useTemplate;
          templatePages: Array<ReportPageTemplate>;
          pageIndex: number;
      }
    | {
          actionType: EditableReportActionType.setDuplicatePageIndex;
          pageIndex: number | null;
      }
    | {
          actionType: EditableReportActionType.addDuplicatePage;
          pageIndexToAdd: number;
          pageIndexToCopy: number;
      }
    | {
          actionType: EditableReportActionType.updateIsResizing;
          isResizing: boolean;
      }
    | {
          actionType: EditableReportActionType.updateResizeHandlePosition;
          position: ResizeHandlePosition | null;
      }
    | {
          actionType: EditableReportActionType.updateIsDragging;
          isDragging: boolean;
      }
    | {
          actionType: EditableReportActionType.updateDraggedCellProps;
          props: {
              row: number;
              col: number;
          } | null;
      }
    | {
          actionType: EditableReportActionType.updateDraggingGhostProperties;
          ghostProps: DraggingGhostProps | null;
      }
    | {
          actionType: EditableReportActionType.setIsTemplateDragging;
          isDragging: boolean;
      }
    | {
          actionType: EditableReportActionType.addTemplate;
          currentPage: EditableReportPage;
          templatePlacement: TemplatePlacement;
          templatePages: Array<ReportPageTemplate>;
      }
    // | {
    //       actionType: EditableReportActionType.editPageDateRange;
    //       dateRangeOption: DateRangeOptions;
    //       pageIndex: number;
    //   }
    | {
          actionType: EditableReportActionType.editWidgetDateRange;
          dateRangeOption: DateRangeOptions;
          widget: EditableWidget;
      }
    | {
          actionType: EditableReportActionType.addCanvasControl;
          type: CanvasControlType;
          name: string;
          value: string;
      }
    | {
          actionType: EditableReportActionType.editCanvasControl;
          canvasControl: EditableCanvasControl;
          changedFields: Omit<Partial<EditableCanvasControl>, "id"> | Partial<CanvasControl>;
      }
    | {
          actionType: EditableReportActionType.editFilter;
          widget: EditableWidget;
          filter: AtomicFilter;
          changedFields: Partial<AtomicFilter>;
      }
    | {
          actionType: EditableReportActionType.addFilter;
          widget: EditableWidget;
          //   parentFilter: AtomicFilter | null;
          filter: AtomicFilter;
      }
    | {
          actionType: EditableReportActionType.deleteFilter;
          widget: EditableWidget;
          filter: AtomicFilter;
      }
    | {
          actionType: EditableReportActionType.setWidgetToDelete;
          widget: EditableWidget | null;
      }
    | {
          actionType: EditableReportActionType.destructiveWidgetTypeChangeWidget;
          widget: EditableWidget | null;
          newWidgetType: WidgetType | null;
      }
    | {
          actionType: EditableReportActionType.setCanvasControls;
          canvasControls: CanvasControls;
      }
    | {
          actionType: EditableReportActionType.toggleCanvasControlActiveStatus;
          filterId: Uuid;
      }
    | {
          actionType: EditableReportActionType.applyCanvasControlToApplicableWidgets;
          canvasControlId: Uuid;
      }
    | {
          actionType: EditableReportActionType.removeUnusedCanvasControls;
      }
    | {
          actionType: EditableReportActionType.generateRequiredL0SkillTemplates;
      }
    | {
          actionType: EditableReportActionType.handleGlobalFiltersLinking;
          widget: EditableWidget;
          newLinkingState: boolean;
      }
    | {
          actionType: EditableReportActionType.handleCanvasControlSelectAll;
          canvasControl: CanvasControl;
          newValue: boolean;
      }
    | {
          actionType: EditableReportActionType.setClearPageIndex;
          pageIndex: number | undefined;
      }
    | {
          actionType: EditableReportActionType.clearPage;
          pageIndex: number | undefined;
      };

export function editableReportReducer(state: EditableReportState, action: EditableReportAction): EditableReportState {
    const newState = structuredClone(state);

    switch (action.actionType) {
        case EditableReportActionType.editWidget: {
            pageLoop: for (const page of newState.pages) {
                for (const widget of page.widgets) {
                    if (action.widget.id == widget.id) {
                        Object.assign(widget, action.changedFields);

                        if (widget.type === WidgetType.table && widget.comparison.enabled === false) {
                            widget.comparisonData = null;
                            widget.comparisonColumnMetadata = null;
                            widget.comparisonColumnAttributeIds = null;
                            widget.comparisonLoadingState = WidgetLoadingState.notStarted;
                            widget.comparisonErrorCode = null;
                        }

                        if (
                            widget.type === WidgetType.table &&
                            "data" in action.changedFields &&
                            action.changedFields.data !== null &&
                            widget.columnMetadata !== null
                        ) {
                            const dataKeys = Object.keys(widget.columnMetadata);
                            widget.columnProperties = reconcileTableColumnProperties(dataKeys, widget.columnProperties);
                        }

                        if (
                            action.changedFields.startDateIso != null ||
                            action.changedFields.endDateIso != null ||
                            action.changedFields.startDateJs != null ||
                            action.changedFields.endDateJs != null
                        ) {
                            widget.dateRange = null;
                        }

                        if (action.changedFields.datasetId !== undefined) {
                            const datasetId = action.changedFields.datasetId;

                            for (const canvasControl of Object.values(newState.canvasControls).filter((canvasControl) => canvasControl.isActive === true)) {
                                if (isStaticCanvasControl(canvasControl) === true) {
                                    continue;
                                }
                                const mapping = canvasControl.dabbaToAttributeMapping[datasetId];
                                if (mapping === undefined || mapping.dimensions.length === 0 || canvasControl.type !== CanvasControlType.multiSelect) {
                                    continue;
                                }

                                for (const dimension of mapping.dimensions) {
                                    const atomicFilter: AtomicFilter = {
                                        id: generateUuid(),
                                        globalFilterId: canvasControl.id,
                                        valueType: BackendFilterValueTypes.globalFilter,
                                        attribute: dimension,
                                        operation: BackendFilterOperations.in,
                                        filterType: BackendFilterTypes.atomic,
                                        value: canvasControl.defaultValue,
                                        tempValue: canvasControl.defaultValue,
                                    };

                                    if (widget.backendFilter === null) {
                                        widget.backendFilter = {
                                            id: generateUuid(),
                                            filterType: BackendFilterTypes.and,
                                            filters: [atomicFilter],
                                        };
                                    } else {
                                        widget.backendFilter.filters.push(atomicFilter);
                                    }
                                }
                            }
                        }

                        if (action.widget.id === newState.currentlySelectedWidget?.id) {
                            newState.currentlySelectedWidget = widget;
                        }
                        break pageLoop;
                    }
                }
            }

            const gridCellToOccupancies = calculateGridDerivedState(newState.pages);

            for (let pageIndex = 0; pageIndex < newState.pages.length; pageIndex++) {
                newState.pages[pageIndex].gridCellToOccupancy = gridCellToOccupancies[pageIndex];
            }

            break;
        }
        case EditableReportActionType.setCurrentlySelectedWidget: {
            const widget = action.widget;

            if (widget?.id === newState.currentlySelectedWidget?.id) {
                break;
            }

            newState.currentlySelectedWidget = widget;

            if (widget !== null) {
                const selectedPage = newState.pages.find((page) => page.widgets.some((pageWidget) => pageWidget.id === widget.id));
                if (selectedPage !== undefined) {
                    newState.currentlySelectedPageId = selectedPage.id;
                }
            }

            break;
        }
        case EditableReportActionType.setCurrentlySelectedPage: {
            newState.currentlySelectedPageId = action.pageId;
            newState.currentlySelectedWidget = null;

            break;
        }
        case EditableReportActionType.addWidget: {
            const actionPageIndex = action.pageIndex;
            const widget = action.widget;

            newState.pages[actionPageIndex].widgets.push(widget);
            newState.currentlySelectedPageId = newState.pages[actionPageIndex].id;
            newState.currentlySelectedWidget = widget;

            const gridCellToOccupancies = calculateGridDerivedState(newState.pages);
            for (let pageIndex = 0; pageIndex < newState.pages.length; pageIndex++) {
                newState.pages[pageIndex].gridCellToOccupancy = gridCellToOccupancies[pageIndex];
            }

            for (const canvasControl of Object.values(newState.canvasControls)) {
                if (canvasControl.type === CanvasControlType.dateRange && canvasControl.isActive === true) {
                    if (canvasControl.defaultDateRange !== null) {
                        const {startDate, endDate} = getDatesForDateRangeOption(canvasControl.defaultDateRange, newState.timezone);
                        widget.dateRangeCanvasControl = canvasControl.id;
                        widget.dateRange = null;
                        widget.startDateIso = startDate;
                        widget.endDateIso = endDate;
                        widget.startDateJs = DateTime.fromISO(startDate).toJSDate();
                        widget.endDateJs = DateTime.fromISO(endDate).toJSDate();
                    } else if (canvasControl.defaultDateRange === null && canvasControl.defaultValue[0] !== null && canvasControl.defaultValue[1] !== null) {
                        const startDate = canvasControl.defaultValue[0];
                        const endDate = canvasControl.defaultValue[1];

                        widget.dateRangeCanvasControl = canvasControl.id;
                        widget.dateRange = null;
                        widget.startDateIso = startDate;
                        widget.endDateIso = endDate;
                        widget.startDateJs = DateTime.fromISO(startDate).toJSDate();
                        widget.endDateJs = DateTime.fromISO(endDate).toJSDate();
                    }
                }

                if (canvasControl.type === CanvasControlType.granularity && canvasControl.isActive === true) {
                    widget.granularity = {
                        type: WidgetValueType.canvasControl,
                        value: canvasControl.id,
                    };
                }
            }

            break;
        }
        case EditableReportActionType.changeLeftPaneTab: {
            newState.leftPane.currentlySelectedTabIndex = action.index;

            break;
        }
        case EditableReportActionType.addBlankPage: {
            const nCols = action.nCols;
            const nRows = action.nRows;
            const gridCellToOccupancy = Array.from({length: nRows}, () => Array(nCols).fill(null));

            const newPage = {
                id: generateUuid(),
                title: `Page ${newState.pages.length + 1}`,
                widgets: [],
                gridCellToOccupancy: gridCellToOccupancy,
                isPageNameEditing: false,
            } satisfies EditableReportPage;

            newState.pages.push(newPage);

            newState.currentlySelectedPageId = newPage.id;
            newState.currentlySelectedWidget = null;

            break;
        }
        case EditableReportActionType.setDeletePageIndex: {
            const pageIndex = action.pageIndex;

            newState.pageIndexToDelete = pageIndex;
            break;
        }
        case EditableReportActionType.deletePage: {
            const deleteIndex = action.deleteIndex;

            const deletedPage = newState.pages[deleteIndex];
            newState.pages.splice(deleteIndex, 1);
            newState.pageIndexToDelete = undefined;

            if (deletedPage?.id === newState.currentlySelectedPageId) {
                newState.currentlySelectedPageId = null;
            }

            break;
        }
        case EditableReportActionType.swapPages: {
            const fromIndex = action.fromIndex;
            const toIndex = action.toIndex;

            [newState.pages[fromIndex], newState.pages[toIndex]] = [newState.pages[toIndex], newState.pages[fromIndex]];
            break;
        }
        // case EditableReportActionType.changePageDates: {
        //     const startDate = action.startDate;
        //     const endDate = action.endDate;
        //     const pageIndex = action.pageIndex;

        //     const page = newState.pages[pageIndex];
        //     if (page == null) {
        //         break;
        //     }
        //     page.startDateJs = startDate;
        //     page.endDateJs = endDate;

        //     page.startDateIso = DateTime.fromJSDate(startDate)
        //         .setZone(newState.timezone)
        //         .toISO() as Iso8601DateTime;
        //     page.endDateIso = DateTime.fromJSDate(endDate)
        //         // Adding this to include the end date as an open interval
        //         .plus({day: 1})
        //         .setZone(newState.timezone)
        //         .toISO() as Iso8601DateTime;

        //     page.dateRange = null;

        //     break;
        // }
        case EditableReportActionType.deleteWidget: {
            const currentlySelectedWidget = action.currentlySelectedWidget;
            if (currentlySelectedWidget == null) {
                break;
            }

            for (const [pageIndex, page] of newState.pages.entries()) {
                for (const [widgetIndex, widget] of page.widgets.entries()) {
                    if (widget.id === currentlySelectedWidget.id) {
                        newState.pages[pageIndex].widgets.splice(widgetIndex, 1);
                        newState.currentlySelectedWidget = null;
                        break;
                    }
                }
            }

            const gridCellToOccupancies = calculateGridDerivedState(newState.pages);
            for (let pageIndex = 0; pageIndex < newState.pages.length; pageIndex++) {
                newState.pages[pageIndex].gridCellToOccupancy = gridCellToOccupancies[pageIndex];
            }

            break;
        }
        case EditableReportActionType.editPage: {
            const pageFromAction = action.page;
            const changedFields = action.changedFields;

            for (const page of newState.pages) {
                if (pageFromAction.id == page.id) {
                    Object.assign(page, changedFields);
                    break;
                }
            }
            break;
        }
        case EditableReportActionType.editReport: {
            const changedFields = action.changedFields;

            Object.assign(newState, changedFields);
            break;
        }
        case EditableReportActionType.changeRightPaneTab: {
            const tabIndex = action.tabIndex;

            newState.rightPane.currentlySelectedTabIndex = tabIndex;
            break;
        }
        case EditableReportActionType.useTemplate: {
            const pageIndex = action.pageIndex;
            const pages = action.templatePages;
            const editableReportPages = pages.map((page) =>
                reportPageToEditableReportPage(reportPageTemplateToReportPage(page), newState.timezone, newState.canvasControls),
            );

            newState.pages.splice(pageIndex + 1, 0, ...editableReportPages);
            break;
        }
        case EditableReportActionType.setDuplicatePageIndex: {
            const pageIndex = action.pageIndex;

            newState.pageIndexToDuplicate = pageIndex;

            break;
        }
        case EditableReportActionType.addDuplicatePage: {
            const addIndex = action.pageIndexToAdd;
            const copyIndex = action.pageIndexToCopy;

            newState.pages = [
                ...newState.pages.slice(0, addIndex),
                {
                    ...newState.pages[copyIndex],
                    widgets: newState.pages[copyIndex]?.widgets.map((widget) => ({
                        ...widget,
                        id: generateUuid(),
                        backendFilter:
                            widget.backendFilter === null || widget.backendFilter === undefined
                                ? null
                                : {
                                      ...widget.backendFilter,
                                      id: generateUuid(),
                                      filters:
                                          widget.backendFilter?.filters.map((filter) => ({
                                              ...filter,
                                              id: generateUuid(),
                                          })) ?? null,
                                  },
                    })),
                    id: generateUuid(),
                },
                ...newState.pages.slice(addIndex),
            ];

            break;
        }
        case EditableReportActionType.updateIsResizing: {
            const value = action.isResizing;

            newState.isResizing = value;

            break;
        }
        case EditableReportActionType.updateResizeHandlePosition: {
            const position = action.position;

            newState.resizeHandlePosition = position;

            break;
        }
        case EditableReportActionType.updateIsDragging: {
            const value = action.isDragging;

            newState.isDragging = value;

            break;
        }
        case EditableReportActionType.updateDraggedCellProps: {
            const props = action.props;

            newState.draggedCellProps = props;

            break;
        }
        case EditableReportActionType.updateDraggingGhostProperties: {
            if (action.ghostProps == null) {
                newState.ghostProps = null;
                break;
            }

            const cellIndex = action.ghostProps.cellIndex;
            const rowSpan = action.ghostProps.rowSpan;
            const colSpan = action.ghostProps.colSpan;
            const origin = action.ghostProps.origin;
            const pageIndex = action.ghostProps.pageIndex;

            newState.ghostProps = {
                cellIndex: cellIndex,
                rowSpan: rowSpan,
                colSpan: colSpan,
                origin: origin,
                pageIndex: pageIndex,
            };

            break;
        }
        case EditableReportActionType.setIsTemplateDragging: {
            const isDragging = action.isDragging;

            newState.isTemplateDragging = isDragging;

            break;
        }
        case EditableReportActionType.addTemplate: {
            const pages = action.templatePages;
            const currentPage = action.currentPage;
            const placement = action.templatePlacement;

            let pageIndex;
            for (let i = 0; i < newState.pages.length; i++) {
                if (newState.pages[i]?.id === currentPage.id) {
                    pageIndex = i;
                    break;
                }
            }

            if (pageIndex == null) {
                break;
            }

            const addIndex = placement === TemplatePlacement.bottom ? pageIndex + 1 : pageIndex;

            const editableReportPages = pages.map((page) => {
                const pageWithNewIds = refreshReportPageIds(reportPageTemplateToReportPage(page));
                return reportPageToEditableReportPage(pageWithNewIds, newState.timezone, newState.canvasControls);
            });

            newState.pages.splice(addIndex, 0, ...editableReportPages);
            break;
        }
        // case EditableReportActionType.editPageDateRange: {
        //     const pageIndex = action.pageIndex;
        //     const option = action.dateRangeOption;

        //     const page = newState.pages[pageIndex];

        //     if (page == null) {
        //         break;
        //     }

        //     const {startDate, endDate} = getDatesForDateRangeOption(
        //         option,
        //         newState.timezone,
        //     );

        //     page.startDateIso = startDate;
        //     page.endDateIso = endDate;
        //     page.startDateJs = DateTime.fromISO(startDate).toJSDate();
        //     page.endDateJs = DateTime.fromISO(endDate).toJSDate();

        //     page.dateRange = option;

        //     break;
        // }
        case EditableReportActionType.editWidgetDateRange: {
            const option = action.dateRangeOption;

            let widget;
            pageLoop: for (const page of newState.pages) {
                for (const pageWidget of page.widgets) {
                    if (action.widget.id === pageWidget.id) {
                        const {startDate, endDate} = getDatesForDateRangeOption(option, newState.timezone);

                        Object.assign(pageWidget, {
                            startDateIso: startDate,
                            endDateIso: endDate,
                            startDateJs: DateTime.fromISO(startDate).toJSDate(),
                            endDateJs: DateTime.fromISO(endDate).toJSDate(),
                            dateRange: option,
                            dateRangeCanvasControl: null,
                        });

                        break pageLoop;
                    }
                }
            }

            break;
        }
        case EditableReportActionType.addCanvasControl: {
            const id = generateUuid();

            const canvasControl: CanvasControl = {
                id: id,
                type: action.type,
                value: action.value,
                name: action.name,
                isActive: false,
                dabbaToAttributeMapping: {},
            } as CanvasControl;

            newState.canvasControls[id] = canvasControl;

            break;
        }
        case EditableReportActionType.editCanvasControl: {
            const canvasControl = structuredClone(action.canvasControl);

            Object.assign(canvasControl, action.changedFields);

            newState.canvasControls[canvasControl.id] = canvasControl;

            if (action.changedFields.defaultValue !== undefined) {
                for (const page of newState.pages) {
                    for (const widget of page.widgets) {
                        if (
                            widget.backendFilter === null ||
                            widget.backendFilter === undefined ||
                            Array.isArray(widget.backendFilter.filters) === false ||
                            widget.backendFilter.filters.length === 0
                        ) {
                            continue;
                        }

                        const filters: Array<AtomicFilter> = widget.backendFilter.filters;
                        for (const filter of filters) {
                            if (filter.valueType !== BackendFilterValueTypes.globalFilter) {
                                continue;
                            }

                            if (filter.globalFilterId === canvasControl.id) {
                                filter.value = canvasControl.defaultValue;
                            }
                        }

                        if (widget.id === newState.currentlySelectedWidget?.id) {
                            newState.currentlySelectedWidget = widget;
                        }
                    }
                }
            }

            break;
        }
        // case EditableReportActionType.editFilter: {
        //     pageLoop: for (const page of newState.pages) {
        //         for (const widget of page.widgets) {
        //             if (action.widget.id == widget.id) {
        //                 if (widget.backendFilter != null) {
        //                     traverseBackendFilter(
        //                         widget.backendFilter as BackendFilter,
        //                         (filter: BackendFilter) => {
        //                             if (filter.id === action.filter.id) {
        //                                 Object.assign(
        //                                     filter,
        //                                     action.changedFields,
        //                                 );
        //                                 return true;
        //                             }
        //                         },
        //                     );
        //                     break pageLoop;
        //                 }
        //             }
        //         }
        //     }
        //     break;
        // }
        // case EditableReportActionType.addFilter: {
        //     pageLoop: for (const page of newState.pages) {
        //         for (const widget of page.widgets) {
        //             if (action.widget.id == widget.id) {
        //                 if (widget.backendFilter == null) {
        //                     widget.backendFilter = {
        //                         id: generateUuid(),
        //                         filterType: BackendFilterTypes.and,
        //                         filters: [action.filter],
        //                     };
        //                 } else {
        //                     traverseBackendFilter(
        //                         widget.backendFilter as BackendFilter,
        //                         (filter: BackendFilter) => {
        //                             if (filter.id === action.parentFilter?.id) {
        //                                 if (
        //                                     filter.filterType ===
        //                                         BackendFilterTypes.and ||
        //                                     filter.filterType ===
        //                                         BackendFilterTypes.or
        //                                 ) {
        //                                     filter.filters.push(action.filter);
        //                                     return true;
        //                                 }
        //                             }
        //                         },
        //                     );
        //                 }
        //                 break pageLoop;
        //             }
        //         }
        //     }
        //     break;
        // }
        case EditableReportActionType.addFilter: {
            const widget = action.widget;
            const filter = action.filter;

            outerLoop: for (const page of newState.pages) {
                for (const currentWidget of page.widgets) {
                    if (currentWidget.id === widget.id) {
                        if (currentWidget.backendFilter == null) {
                            const backendFilter: BackendFilter = {
                                id: generateUuid(),
                                filterType: BackendFilterTypes.and,
                                filters: [filter],
                            };

                            currentWidget.backendFilter = backendFilter;
                            newState.currentlySelectedWidget = currentWidget;
                            break outerLoop;
                        }
                        for (const currentFilter of widget.backendFilter.filters) {
                            if (currentFilter.id === filter.id) {
                                newState.currentlySelectedWidget = currentWidget;
                                break outerLoop;
                            }
                        }

                        currentWidget.backendFilter.filters.push(filter);
                        newState.currentlySelectedWidget = currentWidget;
                        break outerLoop;
                    }
                }
            }
            break;
        }
        case EditableReportActionType.editFilter: {
            const widget = action.widget;
            const filter = action.filter;

            outerLoop: for (const page of newState.pages) {
                for (const currentWidget of page.widgets) {
                    if (currentWidget.id === widget.id) {
                        if (
                            currentWidget.backendFilter != null &&
                            currentWidget.backendFilter.filterType === BackendFilterTypes.and &&
                            currentWidget.backendFilter.filters != null
                        ) {
                            for (const currentFilter of currentWidget.backendFilter.filters) {
                                if (currentFilter.id === filter.id) {
                                    Object.assign(currentFilter, action.changedFields);
                                    newState.currentlySelectedWidget = currentWidget;
                                    break outerLoop;
                                }
                            }
                        }
                    }
                }
            }
            break;
        }
        case EditableReportActionType.deleteFilter: {
            const filter = action.filter;
            const widget = action.widget;

            outerLoop: for (const page of newState.pages) {
                for (const currentWidget of page.widgets) {
                    if (currentWidget.id === widget.id) {
                        if (currentWidget.backendFilter != null) {
                            for (const [currentFilterIndex, currentFilter] of currentWidget.backendFilter.filters.entries()) {
                                if (filter.id === currentFilter.id) {
                                    currentWidget.backendFilter.filters.splice(currentFilterIndex, 1);
                                    newState.currentlySelectedWidget = currentWidget;
                                    break outerLoop;
                                }
                            }
                        }
                    }
                }
            }

            break;
        }
        case EditableReportActionType.setWidgetToDelete: {
            const widget = action.widget;

            newState.widgetToDelete = widget;

            break;
        }
        case EditableReportActionType.destructiveWidgetTypeChangeWidget: {
            const widget = action.widget;
            const newWidgetType = action.newWidgetType;

            if (widget == null || newWidgetType == null) {
                newState.destructiveWidgetTypeChangeDetails = null;
                break;
            }

            newState.destructiveWidgetTypeChangeDetails = {
                currentWidget: widget,
                newWidgetType: newWidgetType,
            };

            break;
        }
        case EditableReportActionType.setCanvasControls: {
            const canvasControls = action.canvasControls;

            newState.canvasControls = canvasControls;

            break;
        }
        case EditableReportActionType.toggleCanvasControlActiveStatus: {
            const filterId = action.filterId;

            for (const canvasControl of Object.values(newState.canvasControls)) {
                if (canvasControl.id === filterId) {
                    Object.assign(canvasControl, {
                        isActive: !canvasControl.isActive,
                    });
                }
            }

            break;
        }
        case EditableReportActionType.applyCanvasControlToApplicableWidgets: {
            const canvasControlId = action.canvasControlId;

            const canvasControl = newState.canvasControls[canvasControlId];
            if (canvasControl === undefined) {
                break;
            }

            if (canvasControl.dabbaToAttributeMapping === undefined) {
                break;
            }

            for (const page of newState.pages) {
                for (const widget of page.widgets) {
                    if (widget.areCanvasControlsLinked === false) {
                        continue;
                    }

                    if (canvasControl.type === CanvasControlType.dateRange && canvasControl.isActive === true) {
                        if (canvasControl.defaultDateRange === null && canvasControl.defaultValue[0] !== null && canvasControl.defaultValue[1] !== null) {
                            widget.startDateIso = canvasControl.defaultValue[0];
                            widget.endDateIso = canvasControl.defaultValue[1];
                            widget.startDateJs = DateTime.fromISO(canvasControl.defaultValue[0]).toJSDate();
                            widget.endDateJs = DateTime.fromISO(canvasControl.defaultValue[1]).toJSDate();
                            widget.dateRange = null;
                            widget.dateRangeCanvasControl = canvasControl.id;
                        } else if (canvasControl.defaultDateRange !== null) {
                            const {startDate, endDate} = getDatesForDateRangeOption(canvasControl.defaultDateRange, newState.timezone);
                            widget.startDateIso = startDate;
                            widget.endDateIso = endDate;
                            widget.startDateJs = DateTime.fromISO(startDate).toJSDate();
                            widget.endDateJs = DateTime.fromISO(endDate).toJSDate();
                            widget.dateRange = canvasControl.defaultDateRange;
                            widget.dateRangeCanvasControl = canvasControl.id;
                        }

                        if (widget.id === newState.currentlySelectedWidget?.id) {
                            newState.currentlySelectedWidget = widget;
                        }

                        continue;
                    } else if (canvasControl.type === CanvasControlType.dateRange && canvasControl.isActive === false) {
                        widget.dateRangeCanvasControl = null;
                        widget.dateRange = canvasControl.dateRange;
                        widget.startDateIso = canvasControl.defaultValue[0];
                        widget.endDateIso = canvasControl.defaultValue[1];
                        widget.startDateJs = DateTime.fromISO(canvasControl.defaultValue[0]).toJSDate();
                        widget.endDateJs = DateTime.fromISO(canvasControl.defaultValue[1]).toJSDate();
                    }

                    if (canvasControl.type === CanvasControlType.granularity && canvasControl.isActive === true) {
                        widget.granularity = {
                            type: WidgetValueType.canvasControl,
                            value: canvasControl.id,
                        };

                        if (widget.id === newState.currentlySelectedWidget?.id) {
                            newState.currentlySelectedWidget = widget;
                        }

                        continue;
                    } else if (canvasControl.type === CanvasControlType.granularity && canvasControl.isActive === false) {
                        widget.granularity = {
                            type: WidgetValueType.intrinsic,
                            value: canvasControl.defaultValue,
                        };
                    }

                    if (widget.datasetId === null) {
                        continue;
                    }

                    const attributes = canvasControl.dabbaToAttributeMapping[widget.datasetId];

                    if (attributes === undefined) {
                        continue;
                    }

                    // Not considering metric filters for now
                    if (attributes.dimensions.length === 0) {
                        continue;
                    }

                    if (widget.backendFilter === null) {
                        widget.backendFilter = {
                            id: generateUuid(),
                            filterType: BackendFilterTypes.and,
                            filters: [],
                        };
                    }

                    if (widget.backendFilter.filters === null) {
                        widget.backendFilter = {
                            ...widget.backendFilter,
                            filters: [],
                        };
                    }

                    if (canvasControl.isActive === false) {
                        canvasControl.defaultValue = [];
                    }

                    for (const dimension of attributes.dimensions) {
                        const atomicFilter: AtomicFilter = {
                            id: generateUuid(),
                            valueType: BackendFilterValueTypes.globalFilter,
                            attribute: dimension,
                            filterType: BackendFilterTypes.atomic,
                            operation: BackendFilterOperations.in,
                            globalFilterId: canvasControl.id,
                            value: [],
                            tempValue: null,
                        };

                        if (widget.id === newState.currentlySelectedWidget?.id) {
                            if (canvasControl.isActive === true) {
                                if (newState.currentlySelectedWidget.backendFilter === null || newState.currentlySelectedWidget.backendFilter === undefined) {
                                    newState.currentlySelectedWidget.backendFilter = {
                                        id: generateUuid(),
                                        filterType: BackendFilterTypes.and,
                                        filters: [],
                                    };
                                }

                                if (widget.backendFilter.filters === null || widget.backendFilter.filters === undefined) {
                                    widget.backendFilter.filters = [atomicFilter];
                                } else {
                                    widget.backendFilter.filters.push(atomicFilter);
                                }
                            } else {
                                if (newState.currentlySelectedWidget.backendFilter === null) {
                                    continue;
                                }

                                const index = widget.backendFilter.filters.findIndex((filter) => filter.globalFilterId === atomicFilter.globalFilterId);

                                widget.backendFilter.filters.splice(index, 1);
                            }

                            newState.currentlySelectedWidget = widget;
                        } else {
                            if (canvasControl.isActive === true) {
                                if (widget.backendFilter.filters == null) {
                                    widget.backendFilter.filters = [atomicFilter];
                                } else {
                                    widget.backendFilter.filters.push(atomicFilter);
                                }
                            } else {
                                const index = widget.backendFilter.filters.findIndex((filter) => filter.globalFilterId === atomicFilter.globalFilterId);

                                widget.backendFilter.filters.splice(index, 1);
                            }
                        }
                    }
                }
            }

            break;
        }
        case EditableReportActionType.removeUnusedCanvasControls: {
            const usedDabbaIdsForCanvasControls: Array<Uuid> = distinct(
                Object.values(newState.canvasControls)
                    .map((canvasControl) => getDatasetIdsForCanvasControl(canvasControl))
                    .flat(),
            );

            const usedDabbaIdsInWidgets: Array<Uuid> = distinct(
                newState.pages
                    .map((page) => {
                        return page.widgets
                            .map((widget) => {
                                if (widget.datasetId === null) {
                                    return null;
                                }

                                return widget.datasetId;
                            })
                            .filter((datasetId) => datasetId !== null);
                    })
                    .flat(),
            );

            const unusedDabbaIdsInCanvasControls = usedDabbaIdsForCanvasControls.filter((item) => usedDabbaIdsInWidgets.includes(item) === false);

            const canvasControlIdsToRemove: Array<Uuid> = [];

            for (const canvasControl of Object.values(newState.canvasControls)) {
                if (isStaticCanvasControl(canvasControl) === true) {
                    continue;
                }
                const datasetIds = getDatasetIdsForCanvasControl(canvasControl);

                const isUnusedFilter = datasetIds.every((datasetId) => unusedDabbaIdsInCanvasControls.includes(datasetId) === true);
                if (isUnusedFilter === true) {
                    canvasControlIdsToRemove.push(canvasControl.id);
                }
            }

            for (const id of canvasControlIdsToRemove) {
                delete newState.canvasControls[id];
            }

            break;
        }
        case EditableReportActionType.generateRequiredL0SkillTemplates: {
            newState.requiredL0SkillTemplates = [];
            break;
        }
        case EditableReportActionType.handleGlobalFiltersLinking: {
            const incomingWidget = action.widget;
            const newLinkingState = action.newLinkingState;

            let widget: EditableWidget | null = null;
            for (const page of newState.pages) {
                for (const currentWidget of page.widgets) {
                    if (currentWidget.id === incomingWidget.id) {
                        widget = currentWidget;
                    }
                }
            }

            if (widget === null) {
                break;
            }

            widget.areCanvasControlsLinked = newLinkingState;

            if (newLinkingState === true) {
                // Link applicable filters to the widget
                for (const canvasControl of Object.values(newState.canvasControls).filter((canvasControl) => canvasControl.isActive === true)) {
                    if (canvasControl.type === CanvasControlType.dateRange) {
                        widget.dateRangeCanvasControl = canvasControl.id;
                    }

                    if (canvasControl.type === CanvasControlType.granularity) {
                        widget.granularity = {
                            type: WidgetValueType.canvasControl,
                            value: canvasControl.id,
                        };
                    }

                    const dabbaToAttributeMapping = canvasControl.dabbaToAttributeMapping[widget.datasetId];
                    if (dabbaToAttributeMapping === undefined) {
                        continue;
                    }

                    if (canvasControl.type === CanvasControlType.multiSelect) {
                        for (const dimension of dabbaToAttributeMapping.dimensions) {
                            const atomicFilter: AtomicFilter = {
                                id: generateUuid(),
                                valueType: BackendFilterValueTypes.globalFilter,
                                attribute: dimension,
                                filterType: BackendFilterTypes.atomic,
                                operation: BackendFilterOperations.in,
                                globalFilterId: canvasControl.id,
                                value: canvasControl.defaultValue,
                                tempValue: null,
                            };

                            if (widget.backendFilter === null || widget.backendFilter === undefined) {
                                widget.backendFilter = {
                                    id: generateUuid(),
                                    filterType: BackendFilterTypes.and,
                                    filters: [],
                                };
                            }

                            widget.backendFilter.filters.push(atomicFilter);
                        }
                    }
                }
            } else {
                // Unlink global filters from the widget
                if (
                    (widget.backendFilter === null ||
                        widget.backendFilter.filters === null ||
                        Array.isArray(widget.backendFilter.filters) === false ||
                        widget.datasetId === null) === false
                ) {
                    const filters: Array<AtomicFilter> = widget.backendFilter.filters;

                    const newFilters: Array<AtomicFilter> = [];
                    for (const filter of filters) {
                        if (filter.valueType !== BackendFilterValueTypes.globalFilter) {
                            newFilters.push(filter);
                        }
                    }
                    widget.backendFilter.filters = newFilters;
                }

                if (widget.dateRangeCanvasControl !== null) {
                    widget.dateRangeCanvasControl = null;
                }

                if (widget.granularity !== undefined && widget.granularity !== null) {
                    if (widget.granularity.type === WidgetValueType.canvasControl) {
                        widget.granularity = {
                            type: WidgetValueType.intrinsic,
                            value: Granularity.day,
                        };
                    }
                }
            }

            if (newState.currentlySelectedWidget !== null && widget.id === newState.currentlySelectedWidget.id) {
                newState.currentlySelectedWidget = widget;
            }
            break;
        }
        case EditableReportActionType.handleCanvasControlSelectAll: {
            const canvasControl = action.canvasControl;
            const newValue = action.newValue;

            if (canvasControl.type !== CanvasControlType.multiSelect) {
                break;
            }

            if (newValue === true) {
                canvasControl.value = canvasControl.defaultValue;
            } else {
                canvasControl.value = [];
            }

            break;
        }
        case EditableReportActionType.setClearPageIndex: {
            const pageIndex = action.pageIndex;

            newState.clearPageIndex = pageIndex;

            break;
        }
        case EditableReportActionType.clearPage: {
            const pageIndex = action.pageIndex;
            if (pageIndex === undefined) {
                break;
            }

            const page = newState.pages[pageIndex];
            if (page === undefined) {
                break;
            }

            page.widgets = [];
            const gridState = calculateGridDerivedState([page]);
            if (gridState[0] === undefined) {
                break;
            }

            page.gridCellToOccupancy = gridState[0];

            break;
        }
        default: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const exhaustiveCheck: never = action;
            throw new Error(`Encountered unexpected action: ${action}`);
        }
    }

    for (const page of newState.pages) {
        const previousPage = state.pages.find((statePage) => statePage.id === page.id);
        if (previousPage === undefined) {
            continue;
        }

        for (const widget of page.widgets) {
            if (widget.type !== WidgetType.table) {
                continue;
            }

            const previousWidget = previousPage.widgets.find((stateWidget) => stateWidget.id === widget.id);
            if (previousWidget === undefined || previousWidget.type !== WidgetType.table) {
                continue;
            }

            if (previousWidget.startDateIso === widget.startDateIso && previousWidget.endDateIso === widget.endDateIso) {
                continue;
            }

            widget.comparison = {
                ...widget.comparison,
                ...getComparisonDateRangeForPrimaryRange(widget.startDateIso, widget.endDateIso, newState.timezone),
            };
        }
    }

    return newState;
}

export function generateEditableReportInitialState({
    pages,
    reportName,
    canvasControls,
    datasets,
    requiredL0SkillTemplates,
    timezone,
    companyId,
}: {
    pages: Array<ReportPage>;
    reportName: string;
    canvasControls: CanvasControls;
    datasets: Array<DatasetMetadata>;
    requiredL0SkillTemplates: Array<Uuid>;
    timezone: string;
    companyId: Uuid;
}): EditableReportState {
    const id = generateUuid();
    let canvasControlsCloned: CanvasControls = structuredClone(canvasControls);
    for (const canvasControl of Object.values(canvasControlsCloned)) {
        canvasControl.value = canvasControl.defaultValue;
    }

    if (Object.values(canvasControlsCloned).length === 0) {
        const dateCanvasControlId = generateUuid();
        const granularityCanvasControlId = generateUuid();
        canvasControlsCloned = {
            [dateCanvasControlId]: {
                id: dateCanvasControlId,
                type: CanvasControlType.dateRange,
                defaultDateRange: DateRangeOptions.monthToDate,
                dateRange: DateRangeOptions.monthToDate,
                isActive: false,
                dabbaToAttributeMapping: {},
                name: "Date",
                value: [null, null],
                defaultValue: [null, null],
            },
            [granularityCanvasControlId]: {
                id: granularityCanvasControlId,
                type: CanvasControlType.granularity,
                dabbaToAttributeMapping: {},
                isActive: false,
                name: "Granularity",
                defaultValue: Granularity.day,
                value: Granularity.day,
                values: Object.values(granularityMetadata).map((value) => value.humanReadableName),
            },
        };
    }

    const clonedPages: Array<EditableReportPage> = pages.map((page) => reportPageToEditableReportPage(page, timezone, canvasControlsCloned));

    const gridCellToOccupancies = calculateGridDerivedState(clonedPages);
    for (let pageIndex = 0; pageIndex < clonedPages.length; pageIndex++) {
        clonedPages[pageIndex].gridCellToOccupancy = gridCellToOccupancies[pageIndex];
    }

    const datasetIdHashSet = new Set<Uuid>();
    for (const page of clonedPages) {
        for (const widget of page.widgets) {
            if (widget.datasetId !== null) {
                datasetIdHashSet.add(widget.datasetId);
            }
        }
    }

    return {
        pages: clonedPages,
        currentlySelectedWidget: null,
        currentlySelectedPageId: null,
        leftPane: {
            currentlySelectedTabIndex: 0,
        },
        rightPane: {
            currentlySelectedTabIndex: 0,
        },
        isReportNameEditing: false,
        title: reportName,
        isResizing: false,
        isDragging: false,
        resizeHandlePosition: null,
        draggedCellProps: null,
        ghostProps: null,
        isTemplateDragging: false,
        canvasControls: canvasControlsCloned,
        datasets: datasets,
        widgetToDelete: null,
        destructiveWidgetTypeChangeDetails: null,
        doUnsavedChangesExist: false,
        requiredL0SkillTemplates: requiredL0SkillTemplates,
        timezone: timezone,
        datasetIdHashSet: datasetIdHashSet,
        companyId: companyId,
    };
}

export function findFilterById(filter: BackendFilter, targetId: Uuid): BackendFilter | null {
    if (filter.id === targetId) {
        return filter;
    }

    if (filter.filterType === BackendFilterTypes.and) {
        for (const subFilter of filter.filters) {
            const result = findFilterById(subFilter, targetId);
            if (result !== null) {
                return result;
            }
        }
    }

    return null;
}
